import {
  listChartAccounts,
  listJournalEntries,
  type ChartAccount,
  type JournalEntry,
} from "@/lib/api";

export type ClosePeriod = { start: string; end: string };
export type NormalBalance = "debit" | "credit";
export type NormalBalanceSource =
  | "account_metadata"
  | "account_type"
  | "account_code"
  | "default";

export type HostLedgerLine = {
  line_id: string;
  journal_date: string;
  status: "posted";
  account_id: string;
  account_code: string;
  account_name: string;
  normal_balance: NormalBalance;
  normal_balance_source: NormalBalanceSource;
  debit_cents: number;
  credit_cents: number;
};

export type HostLedgerEvidence = {
  complete: boolean;
  ledger_lines: HostLedgerLine[];
  warnings: string[];
};

export type HostCloseBooks = HostLedgerEvidence & {
  raw_material_account_code: string;
};

type EntryPageParams = {
  start_date: string;
  end_date: string;
  account_id?: string;
  limit?: number;
};

export type LedgerEvidenceDependencies = {
  listAccounts?: () => Promise<ChartAccount[]>;
  listEntries?: (params: EntryPageParams) => Promise<JournalEntry[]>;
  pageLimit?: number;
  requestCap?: number;
  lineCap?: number;
};

const DAY_MS = 86_400_000;
export const LEDGER_PAGE_LIMIT = 500;
export const LEDGER_REQUEST_CAP = 40;
export const LEDGER_LINE_CAP = 100_000;

const DEBIT_ACCOUNT_TYPES = new Set([
  "asset",
  "expense",
  "cost_of_goods_sold",
  "cost-of-goods-sold",
  "other_expense",
  "other-expense",
]);
const CREDIT_ACCOUNT_TYPES = new Set([
  "liability",
  "equity",
  "revenue",
  "income",
  "sales",
  "other_income",
  "other-income",
]);
const CODE_NORMAL_BALANCE: Readonly<Record<string, NormalBalance>> = {
  "1": "debit",
  "2": "credit",
  "3": "credit",
  "4": "credit",
  "5": "debit",
  "6": "debit",
  "7": "credit",
  "8": "credit",
  "9": "debit",
};

type RequestBudget = { remaining: number };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function parseIsoDay(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const time = Date.parse(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(time)) return null;
  return new Date(time).toISOString().slice(0, 10) === value ? time : null;
}

function isoDay(time: number): string {
  return new Date(time).toISOString().slice(0, 10);
}

function requirePeriod(period: ClosePeriod): ClosePeriod {
  const start = parseIsoDay(period.start);
  const end = parseIsoDay(period.end);
  if (start === null || end === null || start > end) {
    throw new Error("A valid close period is required.");
  }
  return period;
}

function splitPeriod(period: ClosePeriod): [ClosePeriod, ClosePeriod] | null {
  const start = parseIsoDay(period.start);
  const end = parseIsoDay(period.end);
  if (start === null || end === null) return null;
  const days = Math.round((end - start) / DAY_MS);
  if (days <= 0) return null;
  const leftDays = Math.max(1, Math.floor(days / 2));
  const leftEnd = start + leftDays * DAY_MS;
  if (leftEnd >= end) {
    return [
      { start: period.start, end: isoDay(start) },
      { start: isoDay(end), end: period.end },
    ];
  }
  return [
    { start: period.start, end: isoDay(leftEnd) },
    { start: isoDay(leftEnd + DAY_MS), end: period.end },
  ];
}

/** The close-review period is the only period a frame may read ledger
 * evidence for. A workspace page has no close bounds and must not fall back
 * to all history. */
export function closePeriodFromMode(mode: unknown): ClosePeriod | null {
  if (!isRecord(mode) || !isRecord(mode.close) || !isRecord(mode.close.period)) {
    return null;
  }
  const start = typeof mode.close.period.start === "string" ? mode.close.period.start : "";
  const end = typeof mode.close.period.end === "string" ? mode.close.period.end : "";
  const startTime = parseIsoDay(start);
  const endTime = parseIsoDay(end);
  if (startTime === null || endTime === null || startTime > endTime) return null;
  return { start, end };
}

/** Account metadata owns the normal balance. The current chart exposes
 * `account_type`; an explicit `normal_balance` is honoured when a later API
 * adds it. Code-prefix and debit fallbacks are deterministic and reported
 * through `source` so callers can surface the inference. */
export function accountNormalBalance(account: {
  normal_balance?: unknown;
  account_type?: unknown;
  code?: unknown;
}): { normal_balance: NormalBalance; source: NormalBalanceSource } {
  const explicit = typeof account.normal_balance === "string" ? account.normal_balance.trim().toLowerCase() : "";
  if (explicit === "debit" || explicit === "credit") {
    return { normal_balance: explicit, source: "account_metadata" };
  }
  const type = typeof account.account_type === "string" ? account.account_type.trim().toLowerCase() : "";
  if (DEBIT_ACCOUNT_TYPES.has(type)) {
    return { normal_balance: "debit", source: "account_type" };
  }
  if (CREDIT_ACCOUNT_TYPES.has(type)) {
    return { normal_balance: "credit", source: "account_type" };
  }
  const code = typeof account.code === "string" ? account.code.trim() : "";
  const prefix = code.replace(/^0+/, "").slice(0, 1);
  const byCode = CODE_NORMAL_BALANCE[prefix];
  if (byCode) return { normal_balance: byCode, source: "account_code" };
  return { normal_balance: "debit", source: "default" };
}

function normalBalanceWarning(account: ChartAccount, source: NormalBalanceSource, normalBalance: NormalBalance): string | null {
  if (source === "account_metadata" || source === "account_type") return null;
  const basis = source === "account_code" ? "its account code" : "a deterministic debit default";
  return `Account ${account.code} ${account.name} has no recognized normal balance; inferred ${normalBalance} from ${basis}.`;
}

function compareEntries(left: JournalEntry, right: JournalEntry): number {
  return left.entry_date.localeCompare(right.entry_date) || left.id.localeCompare(right.id);
}

function compareLedgerLines(left: HostLedgerLine, right: HostLedgerLine): number {
  return (
    left.journal_date.localeCompare(right.journal_date) ||
    left.account_code.localeCompare(right.account_code) ||
    left.line_id.localeCompare(right.line_id)
  );
}

async function collectPeriodEntries(
  accountId: string,
  period: ClosePeriod,
  listEntries: (params: EntryPageParams) => Promise<JournalEntry[]>,
  pageLimit: number,
  requestCap: number,
  budget: RequestBudget,
): Promise<{ entries: JournalEntry[]; complete: boolean; warnings: string[] }> {
  const byId = new Map<string, JournalEntry>();
  const warnings = new Set<string>();
  const pending: ClosePeriod[] = [{ ...period }];
  let complete = true;

  while (pending.length > 0) {
    if (budget.remaining <= 0) {
      complete = false;
      warnings.add(`Ledger evidence stopped at the ${requestCap}-request safety cap; later period entries may be missing.`);
      break;
    }
    const window = pending.shift()!;
    budget.remaining -= 1;
    const page = await listEntries({
      start_date: window.start,
      end_date: window.end,
      account_id: accountId,
      limit: pageLimit,
    });
    for (const entry of page) {
      if (typeof entry.id === "string" && entry.id) byId.set(entry.id, entry);
    }
    if (page.length < pageLimit) continue;

    const halves = splitPeriod(window);
    if (!halves) {
      complete = false;
      warnings.add(
        `The accounting journal-entry read returned ${pageLimit} entries for ${window.start} and the endpoint has no offset page; later entries for that day may be missing.`,
      );
      continue;
    }
    pending.unshift(halves[0], halves[1]);
  }

  return {
    entries: [...byId.values()].sort(compareEntries),
    complete,
    warnings: [...warnings],
  };
}

async function accountEvidence(
  account: ChartAccount,
  period: ClosePeriod,
  options: LedgerEvidenceDependencies,
  budget: RequestBudget,
): Promise<HostLedgerEvidence> {
  const pageLimit = Math.min(
    LEDGER_PAGE_LIMIT,
    Math.max(1, Math.floor(options.pageLimit ?? LEDGER_PAGE_LIMIT)),
  );
  const requestCap = options.requestCap ?? LEDGER_REQUEST_CAP;
  const lineCap = options.lineCap ?? LEDGER_LINE_CAP;
  const listEntries = options.listEntries ?? listJournalEntries;
  const normal = accountNormalBalance(account);
  const collected = await collectPeriodEntries(
    account.id,
    period,
    listEntries,
    pageLimit,
    requestCap,
    budget,
  );
  const warnings = new Set(collected.warnings);
  const fallbackWarning = normalBalanceWarning(account, normal.source, normal.normal_balance);
  if (fallbackWarning) warnings.add(fallbackWarning);

  const ledgerLines: HostLedgerLine[] = [];
  let lineCapReached = false;
  outer: for (const entry of collected.entries) {
    if (entry.status !== "posted") continue;
    const journalDate = entry.entry_date.slice(0, 10);
    if (journalDate < period.start || journalDate > period.end) continue;
    for (const [index, line] of entry.lines.entries()) {
      if (line.account_id !== account.id) continue;
      if (
        !Number.isSafeInteger(line.debit_cents) ||
        !Number.isSafeInteger(line.credit_cents) ||
        line.debit_cents < 0 ||
        line.credit_cents < 0
      ) {
        throw new Error(`Journal entry ${entry.id} line ${index + 1} has an invalid posted amount.`);
      }
      if (ledgerLines.length >= lineCap) {
        lineCapReached = true;
        break outer;
      }
      ledgerLines.push({
        line_id: `${entry.id}:${index}`,
        journal_date: journalDate,
        status: "posted",
        account_id: account.id,
        account_code: account.code,
        account_name: account.name,
        normal_balance: normal.normal_balance,
        normal_balance_source: normal.source,
        debit_cents: line.debit_cents,
        credit_cents: line.credit_cents,
      });
    }
  }
  if (lineCapReached) {
    warnings.add(`Ledger evidence stopped at the ${lineCap}-line safety cap; later lines may be missing.`);
  }

  return {
    complete: collected.complete && !lineCapReached,
    ledger_lines: ledgerLines,
    warnings: [...warnings],
  };
}

function findAccount(accounts: readonly ChartAccount[], code: string): ChartAccount {
  const account = accounts.find((item) => item.code === code);
  if (!account) throw new Error("That account is not on the chart.");
  return account;
}

/** One account's period-scoped posted evidence. The journal-entry endpoint is
 * the only host contract with date bounds; it exposes a 500-entry response
 * limit and no offset, so a full window is split by date until it drains. */
export async function loadAccountLedgerEvidence(
  accountCode: string,
  period: ClosePeriod,
  options: LedgerEvidenceDependencies = {},
): Promise<HostLedgerEvidence> {
  const code = accountCode.trim();
  if (!code) throw new Error("An account code is required.");
  const closePeriod = requirePeriod(period);
  const accounts = await (options.listAccounts ?? listChartAccounts)();
  const account = findAccount(accounts, code);
  const budget: RequestBudget = { remaining: options.requestCap ?? LEDGER_REQUEST_CAP };
  return accountEvidence(account, closePeriod, options, budget);
}

/** One evidence bundle for every account that contributes to the close:
 * the raw-material account and the direct-allocation accounts. Preview and
 * final-close callers use the same builder; duplicate line ids never attach
 * twice. */
export async function loadCloseBooksEvidence(
  input: {
    period: ClosePeriod;
    rawMaterialAccountCode?: string | null;
    accountCodes?: readonly string[];
  },
  options: LedgerEvidenceDependencies = {},
): Promise<HostCloseBooks> {
  const period = requirePeriod(input.period);
  const rawMaterialAccountCode = (input.rawMaterialAccountCode ?? "").trim();
  const codes = [
    ...new Set(
      [rawMaterialAccountCode, ...(input.accountCodes ?? []).map((code) => code.trim())].filter(
        Boolean,
      ),
    ),
  ];
  if (codes.length === 0) {
    return {
      complete: true,
      raw_material_account_code: rawMaterialAccountCode,
      ledger_lines: [],
      warnings: [],
    };
  }
  const accounts = await (options.listAccounts ?? listChartAccounts)();
  const budget: RequestBudget = { remaining: options.requestCap ?? LEDGER_REQUEST_CAP };
  const lineCap = options.lineCap ?? LEDGER_LINE_CAP;
  const byLineId = new Map<string, HostLedgerLine>();
  const warnings = new Set<string>();
  let complete = true;

  for (const code of codes) {
    const evidence = await accountEvidence(findAccount(accounts, code), period, options, budget);
    complete = complete && evidence.complete;
    for (const warning of evidence.warnings) warnings.add(warning);
    for (const line of evidence.ledger_lines) byLineId.set(line.line_id, line);
    if (byLineId.size >= lineCap) {
      complete = false;
      warnings.add(`Ledger evidence stopped at the ${lineCap}-line safety cap; later lines may be missing.`);
      break;
    }
  }

  return {
    complete,
    raw_material_account_code: rawMaterialAccountCode,
    ledger_lines: [...byLineId.values()].sort(compareLedgerLines),
    warnings: [...warnings],
  };
}

export function readCloseBooksAccountCodes(inputs: Record<string, unknown>): {
  rawMaterialAccountCode: string;
  accountCodes: string[];
} {
  const books = isRecord(inputs.books) ? inputs.books : null;
  const rawMaterialAccountCode =
    books && typeof books.raw_material_account_code === "string"
      ? books.raw_material_account_code.trim()
      : "";
  const codes = new Set<string>();
  if (books && Array.isArray(books.ledger_lines)) {
    for (const line of books.ledger_lines) {
      if (isRecord(line) && typeof line.account_code === "string" && line.account_code.trim()) {
        codes.add(line.account_code.trim());
      }
    }
  }
  const journalInputs = isRecord(inputs.journal_inputs) ? inputs.journal_inputs : null;
  const direct = journalInputs && isRecord(journalInputs.directexall) ? journalInputs.directexall : null;
  if (direct && Array.isArray(direct.allocations)) {
    for (const allocation of direct.allocations) {
      if (isRecord(allocation) && typeof allocation.account_ref === "string" && allocation.account_ref.trim()) {
        codes.add(allocation.account_ref.trim());
      }
    }
  }
  let rawAccount = rawMaterialAccountCode;
  if (!rawAccount && journalInputs) {
    for (const family of ["wip", "directexall", "cogs"]) {
      const spec = isRecord(journalInputs[family]) ? journalInputs[family] : null;
      const accounts = spec && isRecord(spec.accounts) ? spec.accounts : null;
      const raw = accounts && isRecord(accounts.raw_materials) ? accounts.raw_materials : null;
      if (raw && typeof raw.account_ref === "string" && raw.account_ref.trim()) {
        rawAccount = raw.account_ref.trim();
        break;
      }
    }
  }
  if (rawAccount) codes.delete(rawAccount);
  return { rawMaterialAccountCode: rawAccount, accountCodes: [...codes] };
}

/** Merges one evidence bundle into reviewed close inputs. The evidence
 * refreshes account metadata, while an already-reviewed posted amount is
 * preserved, so a retry cannot double-attach or restate a posted line. */
export function mergeCloseBooksEvidence(
  inputs: Record<string, unknown>,
  evidence: HostCloseBooks,
): Record<string, unknown> {
  const existing = isRecord(inputs.books) ? inputs.books : {};
  const merged = new Map<string, unknown>();
  if (Array.isArray(existing.ledger_lines)) {
    for (const line of existing.ledger_lines) {
      if (isRecord(line) && typeof line.line_id === "string") merged.set(line.line_id, line);
    }
  }
  for (const line of evidence.ledger_lines) {
    const known = merged.get(line.line_id);
    if (!isRecord(known)) {
      merged.set(line.line_id, line);
      continue;
    }
    const debit =
      typeof known.debit_cents === "number" && Number.isSafeInteger(known.debit_cents)
        ? known.debit_cents
        : line.debit_cents;
    const credit =
      typeof known.credit_cents === "number" && Number.isSafeInteger(known.credit_cents)
        ? known.credit_cents
        : line.credit_cents;
    merged.set(line.line_id, { ...known, ...line, debit_cents: debit, credit_cents: credit });
  }
  const rawMaterialAccountCode =
    typeof existing.raw_material_account_code === "string" && existing.raw_material_account_code.trim()
      ? existing.raw_material_account_code
      : evidence.raw_material_account_code;
  return {
    ...inputs,
    books: {
      ...existing,
      complete: evidence.complete,
      raw_material_account_code: rawMaterialAccountCode,
      ledger_lines: [...merged.values()],
    },
  };
}
